import { useEffect, useRef, useState } from "react";

import { Column } from "@/components";
import { ArrowLeftIcon, ArrowRightIcon } from "@/components/streamline";
import { Button } from "@/components/ui/button";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";

const REVEAL_DISTANCE_PX = 100;

export const DashboardScheduleWeekExpandFab = ({
  isExpanded,
  onToggle,
}: {
  isExpanded: boolean;
  onToggle: (next: boolean) => void;
}) => {
  /* React Hooks */
  const gapRef = useRef<HTMLDivElement>(null);
  const [isNearby, setIsNearby] = useState(false);

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const bounds = gapRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const horizontalDistance = Math.max(
        bounds.left - event.clientX,
        0,
        event.clientX - bounds.right,
      );
      const verticalDistance = Math.max(
        bounds.top - event.clientY,
        0,
        event.clientY - bounds.bottom,
      );
      setIsNearby(
        Math.hypot(horizontalDistance, verticalDistance) <= REVEAL_DISTANCE_PX,
      );
    };
    const handlePointerLeave = () => setIsNearby(false);
    window.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });
    document.documentElement.addEventListener(
      "pointerleave",
      handlePointerLeave,
    );
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      document.documentElement.removeEventListener(
        "pointerleave",
        handlePointerLeave,
      );
    };
  }, []);

  /* Handlers */
  const handleClick = () => {
    mixpanelUtils.trackButtonClick(
      isExpanded ? "Collapse schedule" : "Expand schedule",
      MIXPANEL_SCREENS.DASHBOARD,
    );
    onToggle(!isExpanded);
  };

  /* Render */
  const render = () => (
    <Column ref={gapRef} className="relative h-full min-h-0 w-full">
      <Button
        type="button"
        variant="ghost"
        onClick={handleClick}
        className={cn(
          "absolute left-1/2 top-3 z-30 h-8 w-8 -translate-x-1/2 items-center justify-center rounded-lever-md border border-lever-border-light bg-lever-surface-default p-0 text-lever-icon-action shadow-sm transition-opacity hover:bg-lever-surface-neutral focus-visible:pointer-events-auto focus-visible:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100",
          isNearby ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        {isExpanded ? (
          <ArrowRightIcon className="h-4 w-4" />
        ) : (
          <ArrowLeftIcon className="h-4 w-4" />
        )}
      </Button>
    </Column>
  );

  return render();
};
